import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { fetchWrapper } from "_helpers";
import { downloadReport } from "../_helpers/fileDownloader";
// import { defaultPageNumber, defaultPageSize } from "_store";

// create slice

const name = "kpo";
const initialState = createInitialState();
const extraActions = createExtraActions();
const extraReducers = createExtraReducers();
const slice = createSlice({ name, initialState, extraReducers });

export const kpoActions = { ...slice.actions, ...extraActions };
export const kpoReducer = slice.reducer;

// implementation

function createInitialState() {
  return {};
}

function createExtraActions() {
  const baseKpoUrl = `${process.env.REACT_APP_API_URL}/kpo`;
  const baseOrderUrl = `${process.env.REACT_APP_API_URL}/orders`;

  return {
    searchCards: searchCards(),
    getPrintingPage: getPrintingPage(),
    getKpoCardsBydIds: getKpoCardsBydIds(),
    deleteKpoCards: deleteKpoCards(),
    createPlannedKpoCards: createPlannedKpoCards(),
    approveKpoCards: approveKpoCards(),
    generateKpoCardsConfirmationAsSender: generateKpoCardsConfirmationAsSender(),
    generateKpoCardsConfirmationAsCarrier: generateKpoCardsConfirmationAsCarrier(),
    assignKpoCardsReceiveConfirmation: assignKpoCardsReceiveConfirmation(),
    changeKpoCardsStatusToTransportConfirmation: changeKpoCardsStatusToTransportConfirmation(),
    withdrawnKpoCards: withdrawnKpoCards(),
    rejectKpoCards: rejectKpoCards(),
    reviseKpoCards: reviseKpoCards(),
    updateKpoCardsData: updateKpoCardsData(),
    downloadConfirmation: downloadConfirmation(),
    downloadPrintingPage: downloadPrintingPage(),
    updateKpoData: updateKpoData(),
    generateKeoCollectedEntriesFromKpoCards: generateKeoCollectedEntriesFromKpoCards(),
    generateKeoForwardedEntriesFromKpoCards: generateKeoForwardedEntriesFromKpoCards()
  };

  function searchCards() {
    return createAsyncThunk(
      `${name}/search-cards`,
      async(payload) =>
        await fetchWrapper.post(
          `${baseKpoUrl}/search-cards`, payload
        )
    );
  }

  function getPrintingPage() {
    return createAsyncThunk(
      `${name}/getPrintingPage`,
      async({ kpoId }) =>
        await fetchWrapper.get(
          `${baseKpoUrl}/card-printing-page?kpoId=${kpoId}`)
    );
  }

  function getKpoCardsBydIds() {
    return createAsyncThunk(
      `${name}/getKpoCardsBydIds`,
      async({ kpoIds }) =>
        await fetchWrapper.post(
          `${baseKpoUrl}/cards`, { kpoIds: kpoIds })
    );
  }

  function deleteKpoCards() {
    return createAsyncThunk(
      `${name}/deleteKpoCards`,
      async({ orderId, kpoIds, performAll }) =>
        await fetchWrapper.patch(
          `${baseKpoUrl}/order/${orderId}/delete`, { orderId, kpoIds, performAll })
    );
  }

  function createPlannedKpoCards() {
    return createAsyncThunk(
      `${name}/createPlannedKpoCards`,
      async({ orderId, payload }) =>
        await fetchWrapper.post(
          `${baseKpoUrl}/order/${orderId}/planned`, payload)
    );
  }

  function approveKpoCards() {
    return createAsyncThunk(
      `${name}/approveKpoCards`,
      async({ orderId, transactionType, kpoIds, performAll }) =>
        await fetchWrapper.patch(
          `${baseKpoUrl}/order/${orderId}/approve`, { orderId, transactionType, kpoIds, performAll })
    );
  }

  function generateKpoCardsConfirmationAsSender() {
    return createAsyncThunk(
      `${name}/generateKpoCardsConfirmationAsSender`,
      async({ orderId, kpoItems, performAll }) =>
        await fetchWrapper.patch(
          `${baseKpoUrl}/order/${orderId}/sender/generate-confirmation`, { orderId, kpoItems, performAll })
    );
  }

  function generateKpoCardsConfirmationAsCarrier() {
    return createAsyncThunk(
      `${name}/generateKpoCardsConfirmationAsCarrier`,
      async({ orderId, kpoItems, performAll }) =>
        await fetchWrapper.patch(
          `${baseKpoUrl}/order/${orderId}/carrier/generate-confirmation`, { orderId, kpoItems, performAll })
    );
  }

  function assignKpoCardsReceiveConfirmation() {
    return createAsyncThunk(
      `${name}/assignReceiveConfirmation`,
      async({ orderId, transactionType, kpoItems, performAll }) =>
        await fetchWrapper.patch(
          `${baseKpoUrl}/order/${orderId}/assign-receive-confirmation`, { orderId, transactionType, kpoItems, performAll })
    );
  }

  function changeKpoCardsStatusToTransportConfirmation() {
    return createAsyncThunk(
      `${name}/changeKpoCardsStatusToTransportConfirmation`,
      async({ orderId, kpoIds, transactionType, performAll }) =>
        await fetchWrapper.patch(
          `${baseKpoUrl}/order/${orderId}/confirm-transport`, { orderId, kpoIds, transactionType, performAll })
    );
  }

  function withdrawnKpoCards() {
    return createAsyncThunk(
      `${name}/withdrawnKpoCards`,
      async({ orderId, kpoItems, performAll }) =>
        await fetchWrapper.patch(
          `${baseKpoUrl}/order/${orderId}/withdrawn`, { orderId, kpoItems, performAll })
    );
  }

  function rejectKpoCards() {
    return createAsyncThunk(
      `${name}/rejectKpoCards`,
      async({ orderId, kpoItems, performAll }) =>
        await fetchWrapper.patch(
          `${baseKpoUrl}/order/${orderId}/reject`, { orderId, kpoItems, performAll })
    );
  }

  function reviseKpoCards() {
    return createAsyncThunk(
      `${name}/reviseKpoCards`,
      async({ orderId, kpoItems, performAll }) =>
        await fetchWrapper.patch(
          `${baseKpoUrl}/order/${orderId}/revise`, { orderId, kpoItems, performAll })
    );
  }

  function updateKpoCardsData() {
    return createAsyncThunk(
      `${name}/updateKpoCardsData`,
      async({ orderId, kpoItems, performAll }) =>
        await fetchWrapper.patch(
          `${baseKpoUrl}/order/${orderId}/update`, { orderId, kpoItems, performAll })
    );
  }

  function downloadConfirmation() {
    return createAsyncThunk(
      `${name}/downloadConfirmation`,
      async({ kpoId, cardNumber }) => await fetchWrapper.get(`${baseKpoUrl}/${kpoId}/download-confirmation?cardNumber=${cardNumber}`));
  }

  function downloadPrintingPage() {
    return createAsyncThunk(
      `${name}/downloadPrintingPage`,
      async({ kpoId, cardNumber }) => await fetchWrapper.get(`${baseKpoUrl}/${kpoId}/download-printing-page?cardNumber=${cardNumber}`));
  }

  function updateKpoData() {
    return createAsyncThunk(`${name}/updateData`, async ({ orderId, payload }) => {
      return await fetchWrapper.patch(`${baseKpoUrl}/order/${orderId}/data`, payload);
    });
  }

  function generateKeoCollectedEntriesFromKpoCards() {
    return createAsyncThunk(`${name}/generateKeoCollectedEntriesFromKpoCards`, async ({ orderId, kpoItems, performAll }) => {
      return await fetchWrapper.post(`${baseOrderUrl}/${orderId}/kpo/keo/collected/create-entry`, { orderId, kpoItems, performAll });
    });
  }

  function generateKeoForwardedEntriesFromKpoCards() {
    return createAsyncThunk(`${name}/generateKeoForwardedEntriesFromKpoCards`, async ({ orderId, kpoItems, performAll }) => {
      return await fetchWrapper.post(`${baseOrderUrl}/${orderId}/kpo/keo/forwarded/create-entry`, { orderId, kpoItems, performAll });
    });
  }
}

function createExtraReducers() {
  return {
    ...downloadConfirmation(),
    ...downloadPrintingPage()
  };

  function downloadConfirmation() {
    var { fulfilled } = extraActions.downloadConfirmation;
    return {
      [fulfilled]: (state, action) => {
        const { payload } = action;
        downloadReport(payload);
      },
    };
  }

  function downloadPrintingPage() {
    var { fulfilled } = extraActions.downloadPrintingPage;
    return {
      [fulfilled]: (state, action) => {
        const { payload } = action;
        downloadReport(payload);
      },
    };
  }
}